import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Spin } from 'antd';
import * as assistantApi from '../api/assistant';
import { useAuth } from '../context/AuthContext';

// CID Copilot AI — full-screen chat sheet over the agent-scoped GLM tool-loop
// (cid-agent-server assistant.service.js): sales, listings, forms, letters,
// earnings, team. Replies may carry tappable link cards (deep links into the
// app). Text input only — voice was removed because the Web Speech API cannot
// auto-detect the spoken language (EN/BM/中文/தமிழ்).

// Local-only greeting (never sent to the model) — personalised with the logged-in
// agent's name and rank, mirroring what the server-side prompt knows about them.
// Friendly nouns for the "checked:" sources row (from the done event's consulted list).
const CONSULTED_LABEL = {
  get_my_dashboard: 'dashboard', list_my_sales: 'your sales', get_sale: 'sale detail', get_sale_finance: 'sale finances',
  list_my_listings: 'your listings', search_listings: 'listings search', get_listing: 'listing detail',
  list_my_booking_forms: 'booking forms', get_booking_form: 'booking form', list_my_letters: 'co-agency letters', get_letter: 'co-agency letter',
  list_company_documents: 'document library', get_document_fields: 'document fields',
  get_my_earnings: 'your earnings', list_earnings_by_sale: 'commissions', list_my_override_earnings: 'overrides', list_earnings_statements: 'statements',
  get_my_team: 'your team', search_company_knowledge: 'company handbook', get_career_progress: 'career progress',
  simulate_commission: 'simulator', simulate_tier: 'simulator', list_notifications: 'notifications', search_contacts: 'contacts',
};
const consultedText = (list) => [...new Set((list || []).map((t) => CONSULTED_LABEL[t] || null).filter(Boolean))].join(' · ');

// Conversation persists per agent so closing the sheet doesn't wipe the thread.
const STORE_KEY = (agentId) => `cid.copilot.chat.${agentId || 'anon'}`;

const RANK_WORD = { TL: 'Team Leader', CD: 'Branch Head' };
const buildGreeting = (agent) => {
  const name = agent?.display_name || agent?.full_name;
  const rank = RANK_WORD[agent?.management_rank];
  return {
    role: 'assistant',
    greeting: true,
    content: `Hi${name ? ` ${name}` : ''}${rank ? ` (${rank})` : ''}, I'm CID Copilot AI. Ask me about your sales, listings, earnings, team or pending work — or describe what your client needs and I'll match listings. Any language works — English, Bahasa, 中文 or தமிழ்.`,
  };
};

/**
 * `embedded` — render bare, filling whatever container holds it, instead of as a
 * fixed full-screen rising sheet. The desktop shell puts it inside an end Drawer
 * (the drawer owns the entry animation and the scrim); the phone leaves it off
 * and keeps the sheet exactly as it was.
 */
export default function ListingAssistant({ onClose, embedded = false }) {
  const nav = useNavigate();
  const { agent } = useAuth();
  const [messages, setMessages] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORE_KEY(agent?.id)) || 'null');
      if (Array.isArray(saved) && saved.length) return saved.filter((m) => !m.streaming);
    } catch (_) { /* corrupted store */ }
    return [buildGreeting(agent)];
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const stickRef = useRef(true); // follow the stream only while the user is near the bottom
  const abortRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY(agent?.id), JSON.stringify(messages.filter((m) => !m.streaming).slice(-40))); } catch (_) { /* private mode */ }
  }, [messages, agent?.id]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  useEffect(() => {
    if (stickRef.current) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const newChat = () => {
    abortRef.current?.abort();
    setMessages([buildGreeting(agent)]);
    try { localStorage.removeItem(STORE_KEY(agent?.id)); } catch (_) { /* ignore */ }
  };

  // One assistant turn over an explicit message list (used by send AND retry, so
  // retrying never duplicates the user's message).
  const runTurn = async (next) => {
    setSending(true);
    stickRef.current = true;
    const payload = { messages: next.filter((m) => !m.greeting && !m.error && !m.stopped).map((m) => ({ role: m.role, content: m.content })) };

    // Live placeholder bubble: shows activity status while tools run, then the
    // answer text as it streams in; the 'done' event swaps in the canonical reply.
    const live = { role: 'assistant', content: '', status: 'Thinking…', streaming: true, cards: [] };
    setMessages([...next, live]);
    const patchLive = (patch) => setMessages((s) => {
      const arr = [...s];
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i].streaming) { arr[i] = { ...arr[i], ...patch }; break; }
      }
      return arr;
    });

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await assistantApi.streamCopilot(payload, {
        onStatus: (label) => patchLive({ status: label }),
        onDelta: (t) => setMessages((s) => {
          const arr = [...s];
          for (let i = arr.length - 1; i >= 0; i--) {
            if (arr[i].streaming) { arr[i] = { ...arr[i], status: null, content: arr[i].content + t }; break; }
          }
          return arr;
        }),
        onDone: (d) => patchLive({ streaming: false, status: null, content: d.reply || 'Sorry — I got an empty reply. Try again?', cards: d.cards || [], action: d.action || null, log_id: d.log_id || null, consulted: d.consulted || [] }),
      }, controller.signal);
    } catch (streamErr) {
      if (streamErr?.name === 'AbortError') {
        // User tapped Stop — keep whatever streamed in, mark it resumable.
        patchLive((() => ({ streaming: false, status: null, stopped: true }))());
        setMessages((s) => s.map((m) => (m.stopped && !m.content ? { ...m, content: 'Stopped.' } : m)));
      } else {
        // Stream unavailable (proxy/older server) → plain request as fallback.
        try {
          const r = await assistantApi.askCopilot(payload);
          const d = r?.data ?? r;
          patchLive({ streaming: false, status: null, content: d.reply || 'Sorry — I got an empty reply. Try again?', cards: d.cards || [], action: d.action || null, log_id: d.log_id || null, consulted: d.tools_used || [] });
        } catch (e) {
          patchLive({ streaming: false, status: null, content: e.message || 'The assistant is unavailable right now.', error: true });
        }
      }
    } finally {
      abortRef.current = null;
      setSending(false);
    }
  };

  const send = (text) => {
    const content = String(text || '').trim();
    if (!content || sending) return;
    setInput('');
    runTurn([...messages, { role: 'user', content }]);
  };

  const stopStreaming = () => abortRef.current?.abort();

  // Drop trailing failed/stopped bubbles and re-run from the last user message.
  const retry = () => {
    if (sending) return;
    const arr = [...messages];
    while (arr.length && arr[arr.length - 1].role === 'assistant' && (arr[arr.length - 1].error || arr[arr.length - 1].stopped)) arr.pop();
    if (!arr.length || arr[arr.length - 1].role !== 'user') return;
    runTurn(arr);
  };

  const copyMessage = async (m) => {
    try { await navigator.clipboard.writeText(m.content || ''); } catch (_) { /* clipboard denied */ }
  };


  const openCard = (c) => { onClose(); nav(c.path, c.prefill ? { state: { prefill: c.prefill } } : undefined); };

  // Confirm-button write proposal (ACTION contract): Confirm executes server-side
  // (confirmed:true injected there), Cancel just dismisses.
  const resolveAction = async (msgIndex, approve) => {
    const m = messages[msgIndex];
    if (!m?.action) return;
    const mark = (patch) => setMessages((s) => s.map((x, i) => (i === msgIndex ? { ...x, ...patch } : x)));
    if (!approve) { mark({ actionState: 'cancelled' }); return; }
    mark({ actionState: 'running' });
    try {
      const r = await assistantApi.runAction(m.action);
      const d = r?.data ?? r;
      mark({ actionState: 'done' });
      setMessages((s) => [...s, { role: 'assistant', content: d.message || 'Done.', cards: d.card ? [d.card] : [] }]);
    } catch (e) {
      mark({ actionState: null });
      setMessages((s) => [...s, { role: 'assistant', content: e.message || 'That action failed — please try again.', error: true }]);
    }
  };

  const giveFeedback = async (msgIndex, rating) => {
    const m = messages[msgIndex];
    if (!m?.log_id || m.fb) return;
    setMessages((s) => s.map((x, i) => (i === msgIndex ? { ...x, fb: rating } : x)));
    try { await assistantApi.sendFeedback(m.log_id, rating); } catch (_) { /* best-effort */ }
  };

  return (
    <div
      className={embedded ? undefined : 'sheet-panel'}
      style={embedded
        ? { height: '100%', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }
        : { position: 'fixed', inset: 0, zIndex: 4600, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}
    >
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', paddingTop: embedded ? 14 : 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div onClick={onClose} title={embedded ? 'Close' : undefined} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          {embedded ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 5 8 12 15 19" /></svg>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>CID Copilot AI</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>your CID Realtors work assistant</div>
        </div>
        {messages.some((m) => m.role === 'user') && (
          <div onClick={newChat} title="New chat" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            New chat
          </div>
        )}
      </div>

      {/* messages */}
      <div ref={scrollRef} onScroll={onScroll} className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '86%', padding: '10px 13px', borderRadius: 14, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
              background: m.role === 'user' ? 'var(--primary)' : 'var(--surface)',
              color: m.role === 'user' ? '#fff' : (m.error ? 'var(--bad)' : 'var(--text)'),
              border: m.role === 'user' ? 'none' : '1px solid var(--border)',
              borderBottomRightRadius: m.role === 'user' ? 4 : 14,
              borderBottomLeftRadius: m.role === 'user' ? 14 : 4,
            }}>
              {m.status && !m.content ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)' }}>
                  <Spin size="small" /> {m.status}
                </span>
              ) : (
                <>{m.content}{m.streaming && m.content ? ' ▌' : ''}</>
              )}
            </div>

            {m.streaming && (
              <div onClick={stopStreaming} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer', background: 'var(--surface)' }}>
                <span style={{ width: 9, height: 9, background: 'var(--bad)', borderRadius: 2 }} /> Stop
              </div>
            )}

            {(m.error || m.stopped) && !sending && (
              <div onClick={retry} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--primary)', border: '1px solid var(--border)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer', background: 'var(--surface)' }}>
                ↻ Retry
              </div>
            )}
            {(m.cards || []).map((c, j) => (
              <div key={j} onClick={() => openCard(c)} style={{ width: '86%', background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 15px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.title}</div>
                  {c.subtitle && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{c.subtitle}</div>}
                </div>
                <span style={{ color: 'var(--primary)', flex: 'none' }}>›</span>
              </div>
            ))}

            {/* Confirm-button write proposal */}
            {m.action && (
              <div style={{ width: '86%', background: 'var(--surface)', border: '1.5px solid var(--primary)', borderRadius: 12, padding: '14px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{m.action.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, maxHeight: 120, overflowY: 'auto' }}>
                  {Object.entries(m.action.args || {}).filter(([k]) => k !== 'confirmed').map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: 8 }}>
                      <span style={{ flex: 'none', minWidth: 96 }}>{k.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text)', wordBreak: 'break-word' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                    </div>
                  ))}
                </div>
                {m.actionState === 'done' ? (
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ok)' }}>Confirmed ✓</div>
                ) : m.actionState === 'cancelled' ? (
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--muted)' }}>Cancelled</div>
                ) : (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" disabled={m.actionState === 'running'} onClick={() => resolveAction(i, true)} style={{ flex: 1, border: 'none', borderRadius: 9, padding: '8px 0', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', opacity: m.actionState === 'running' ? 0.6 : 1 }}>
                      {m.actionState === 'running' ? 'Working…' : 'Confirm'}
                    </button>
                    <button type="button" disabled={m.actionState === 'running'} onClick={() => resolveAction(i, false)} style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 9, padding: '8px 0', background: 'var(--surface2)', color: 'var(--text)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* answer footer: sources consulted + copy + thumbs */}
            {m.role === 'assistant' && !m.streaming && !m.error && !m.greeting && m.content && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 4, maxWidth: '86%' }}>
                {consultedText(m.consulted) && (
                  <span style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ⓘ checked: {consultedText(m.consulted)}
                  </span>
                )}
                <span onClick={() => copyMessage(m)} title="Copy" style={{ cursor: 'pointer', color: 'var(--muted)', display: 'flex', flex: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                </span>
                {m.log_id && [[1, '👍'], [-1, '👎']].map(([r, icon]) => (
                  <span
                    key={r}
                    onClick={() => giveFeedback(i, r)}
                    style={{ fontSize: 13, flex: 'none', cursor: m.fb ? 'default' : 'pointer', opacity: m.fb ? (m.fb === r ? 1 : 0.25) : 0.55, filter: m.fb && m.fb !== r ? 'grayscale(1)' : 'none' }}
                  >
                    {icon}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* suggested starters — until the first question is asked */}
        {!sending && !messages.some((m) => m.role === 'user') && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 2 }}>
            {[
              "What's pending for me?",
              'My earnings this month',
              'Simulate commission on an RM800k sale',
              'Find condos in KL under RM500k',
              'How do I get promoted to TL?',
              'Which documents can I sign in the app?',
            ].map((q) => (
              <span key={q} onClick={() => send(q)} style={{ fontSize: 12.5, padding: '7px 12px', borderRadius: 999, background: 'var(--chip)', color: 'var(--primary)', fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)' }}>
                {q}
              </span>
            ))}
          </div>
        )}
        <div style={{ height: 4 }} />
      </div>

      {/* input row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '14px 15px', paddingBottom: embedded ? 14 : 'calc(10px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)', flex: 'none' }}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={() => send(input)}
          placeholder="e.g. condo in KL under RM600k, 3 beds"
          disabled={sending}
        />
        <div onClick={() => send(input)} style={{ width: 40, height: 40, borderRadius: 12, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--primary)', color: '#fff', opacity: input.trim() && !sending ? 1 : 0.45 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        </div>
      </div>
    </div>
  );
}
